import React, { useEffect, useRef, useState } from 'react';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { X, Check, ChevronDown } from 'lucide-react';
import { AGREEMENT_DOCS, AGREEMENT_VERSIONS, storeAcceptance, type AgreementType } from '../../legal';
import { agreementApi } from '../../services/agreementApi';
import { MarkdownView } from './MarkdownView';

interface Props {
  open: boolean;
  /** Which documents to display & require acceptance for. Defaults to web set. */
  agreementTypes?: AgreementType[];
  /** Pre-registration only: pin the acceptance to this email so backend can backfill user_id. */
  email?: string;
  onAccept: () => void;
  onDecline?: () => void;
  /** If true, the close button is hidden — user must accept or decline. */
  blocking?: boolean;
}

// Generate (and persist) a per-browser session token used to pin pre-login
// acceptances. Survives page reloads but tied to localStorage origin.
function getSessionToken(): string {
  const KEY = 'ealoom.agreementSession.v1';
  let token = localStorage.getItem(KEY);
  if (!token) {
    token = `s_${Math.random().toString(36).slice(2)}_${Date.now().toString(36)}`;
    localStorage.setItem(KEY, token);
  }
  return token;
}

export const TermsAcceptanceModal: React.FC<Props> = ({
  open,
  agreementTypes,
  email,
  onAccept,
  onDecline,
  blocking = true,
}) => {
  // Default to the web set (Terms + Privacy). Can be overridden for legacy
  // users who only need to accept one of them after a partial bump.
  const types: AgreementType[] = agreementTypes && agreementTypes.length > 0
    ? agreementTypes
    : ['web_tos', 'privacy_policy'];

  const [activeTab, setActiveTab] = useState<AgreementType>(types[0]);
  const [agreed, setAgreed] = useState<Record<string, boolean>>({});
  const [scrolledToBottom, setScrolledToBottom] = useState<Record<string, boolean>>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // If types prop changes (e.g., re-opened with different gap), reset state.
  useEffect(() => {
    setActiveTab(types[0]);
    setAgreed({});
    setScrolledToBottom({});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [types.join(',')]);

  // Reset scroll-to-bottom tracking when switching tabs.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [activeTab]);

  // Detect scroll-to-bottom (within 24px) — required to enable agreement checkbox.
  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
    if (atBottom && !scrolledToBottom[activeTab]) {
      setScrolledToBottom((prev) => ({ ...prev, [activeTab]: true }));
    }
  };

  const scrollToBottom = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
    }
  };

  const canAccept = types.every((t) => agreed[t] && scrolledToBottom[t]);

  const handleAccept = async () => {
    if (!canAccept || submitting) return;
    setSubmitting(true);
    setError(null);

    // Optimistic local persistence — guarantees we don't re-prompt on next page load
    // even if the network call fails.
    storeAcceptance(types);

    try {
      await agreementApi.accept({
        acceptances: types.map((t) => ({ agreement_type: t, version: AGREEMENT_VERSIONS[t] })),
        email: email || undefined,
        session_token: getSessionToken(),
        locale: navigator.language,
      });
    } catch (err) {
      // Non-blocking: local acceptance is recorded; backend sync can be retried
      // post-login if needed. Surface a soft warning but allow proceeding.
      console.warn('Failed to record agreement acceptance to backend:', err);
    }

    setSubmitting(false);
    onAccept();
  };

  if (!open) return null;

  const activeDoc = AGREEMENT_DOCS[activeTab];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Before you continue</h2>
            <p className="text-sm text-gray-600 mt-1">
              Please review and accept the EAloom Terms of Service and Privacy Policy.
            </p>
          </div>
          {!blocking && onDecline && (
            <button
              type="button"
              onClick={onDecline}
              className="text-gray-400 hover:text-gray-600 p-1"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 bg-gray-50">
          {types.map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              className={`flex-1 px-4 py-3 text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                activeTab === tab
                  ? 'bg-white text-green-700 border-b-2 border-green-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {AGREEMENT_DOCS[tab].title}
              {scrolledToBottom[tab] && <Check className="w-4 h-4 text-green-600" />}
            </button>
          ))}
        </div>

        {/* Document body — scrollable */}
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="flex-1 overflow-y-auto px-6 py-4 text-sm text-gray-800"
          style={{ scrollBehavior: 'smooth' }}
        >
          <MarkdownView content={activeDoc.content} />
          <div className="text-center text-xs text-gray-500 italic py-4 border-t border-gray-100 mt-6">
            — End of {activeDoc.title} (v{activeDoc.version}) —
          </div>
        </div>

        {/* Scroll-to-bottom hint */}
        {!scrolledToBottom[activeTab] && (
          <button
            type="button"
            onClick={scrollToBottom}
            className="mx-6 my-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800 flex items-center justify-center gap-2 hover:bg-amber-100"
          >
            <ChevronDown className="w-4 h-4" />
            Please scroll to the bottom to enable acceptance
          </button>
        )}

        {/* Footer / Acceptance */}
        <div className="border-t border-gray-200 bg-gray-50 px-6 py-4">
          {error && (
            <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
              {error}
            </div>
          )}

          <div className="space-y-2 mb-4">
            {types.map((t) => (
              <label key={t} className="flex items-start gap-3 cursor-pointer text-sm text-gray-800">
                <Checkbox
                  checked={!!agreed[t]}
                  onCheckedChange={(c) => setAgreed((prev) => ({ ...prev, [t]: !!c }))}
                  disabled={!scrolledToBottom[t]}
                  className="mt-0.5"
                />
                <span>
                  I have read and agree to the{' '}
                  <button
                    type="button"
                    onClick={() => setActiveTab(t)}
                    className="text-green-700 underline hover:text-green-800"
                  >
                    {AGREEMENT_DOCS[t].title}
                  </button>
                  {' '}(v{AGREEMENT_VERSIONS[t]})
                </span>
              </label>
            ))}
          </div>

          <div className="flex items-center gap-3">
            {!blocking && onDecline && (
              <Button
                type="button"
                onClick={onDecline}
                variant="outline"
                className="flex-1 h-11"
              >
                Decline
              </Button>
            )}
            <Button
              type="button"
              onClick={handleAccept}
              disabled={!canAccept || submitting}
              className="flex-1 h-11 bg-green-600 hover:bg-green-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Recording…' : 'I Agree & Continue'}
            </Button>
          </div>

          <p className="text-xs text-gray-500 mt-3 text-center">
            By clicking "I Agree", you agree to be bound by both documents above. Your acceptance is logged with a timestamp.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TermsAcceptanceModal;
